import "babel-polyfill";
import Vue from "vue";
import * as Sentry from "@sentry/vue";
import { BrowserTracing } from "@sentry/tracing";
import { CaptureConsole } from '@sentry/integrations';
import './../../../sass/output/frontend.scss';

//Vue extensions
import VueRouter from 'vue-router';
import Vuex from 'vuex';

//Pages
import Frontpage from './pages/frontpage.vue';
import HotelPage from './pages/hotels.vue';
import AgencyPage from './pages/agency-view.vue';
import BookingPage from './pages/booking.vue';
import MySkov from './pages/myskov.vue';
import OrderPage from './pages/completed-order.vue';


//Global components
import MsgBar from './components/msg-bar.vue';
import Modal from './components/modal.vue';

//Frontpage components
import search from './components/frontpage/frontpage-search.vue';
import PopularDestinations from './components/frontpage/popular-destinations.vue';
import PopularFilters from './components/frontpage/popular-filters.vue';
import NewsletterSignup from './components/frontpage/newsletter-signup.vue';
import TrustpilotBlock from './components/frontpage/trustpilot-block.vue';
import PageSearchWidgetModal from './components/frontpage/page-search-widget-modal.vue';

import Validate from 'validate.js';
import * as VueGoogleMaps from 'vue2-google-maps';
import Moment from 'moment';
import API from './skovapi.js';
import _ from 'underscore';
import './directives.js';
import JwtDecode from 'jwt-decode';
import { Carousel, Slide } from 'vue-carousel/src/index.js';
import CookieJS from 'js-cookie';
import VueAnalytics from 'vue-analytics';
import VueGtm from '@gtm-support/vue2-gtm';

Moment.locale('da');

Validate.extend(Validate.validators.datetime, {
    // The value is guaranteed not to be null or undefined but otherwise it
    // could be anything.
    parse: function(value, options) {
        return +Moment(value);
    },
    // Input is a unix timestamp
    format: function(value, options) {
        var format = options.dateOnly ? "YYYY-MM-DD" : "YYYY-MM-DD hh:mm:ss";
        return Moment(value).format(format);
    }
  });

Vue.use(VueRouter);
Vue.use(Vuex);
Vue.use(VueGoogleMaps, {
    load: {
        key: APP_CONFIG.GOOGLE_MAPS_KEY,
        libraries: 'places',
        language: 'da'
    }
});

window.MOCK_TYPE = null;

const store = new Vuex.Store({
    state: {
        favorites: [ ],
        travelQuery: { },
        offers: [ ],
        selectedAgencyOffer: { },
        selectedBookingOffer: { },
        bookedOffer: { },
        filters: { },
        destination: { },
        totalOfferCount: 0,
        currentPage: 1,
        verifyPercent: 0,
        stateTimestamp: 0,
        countries: [ ],
        authToken: null,
        loggedInUser: { },
        userSID: null,
        savedBookingFormData: { }
    },
    mutations: {
        filters(state, filters) {
            state.filters = filters;
        },
        offers(state, offers) {
            state.offers = offers;
        },
        destination(state, destination) {
            state.destination = destination;
        },
        travelQuery(state, query) {
            state.travelQuery = query;
        },
        totalOfferCount(state, count) {
            state.totalOfferCount = count;
        },
        nextPage(state) {
            state.currentPage += 1;
        },
        resetPage(state) {
            state.currentPage = 1;
        },
        verifyPercent(state, percentComplete) {
            state.verifyPercent = percentComplete.toFixed(0);
        },
        selectedBookingOffer(state, offer) {
            state.selectedBookingOffer = Object.assign({}, offer);
            state.stateTimestamp = +Moment();
        },
        countries(state, countries) {
            state.countries = countries;
        },
        authToken(state, token) {
            if(token) {
                state.authToken = token;
                API.setToken(token);
                CookieJS.set(APP_CONFIG.AUTH_COOKIE_NAME, token);
                if(typeof MENU_COMPONENT !== 'undefined') {
                    MENU_COMPONENT.$emit('userlogin');
                }
            } else {
                state.authToken = null;
                API.removeToken();
                CookieJS.remove(APP_CONFIG.AUTH_COOKIE_NAME);
                if(typeof MENU_COMPONENT !== 'undefined') {
                    MENU_COMPONENT.$emit('userlogout');
                }
            }
        },
        loggedInUser(state, user) {
            state.loggedInUser = user;
        },
        bookedOffer(state, data) {
            state.bookedOffer = data;
        },
        selectedAgencyOffer(state, data) {
            state.selectedAgencyOffer = data;
        },
        favorites(state, list) {
            state.favorites = list;
        },
        userSID(state, userSID) {
            if(userSID) {
                let expires = Moment().add(1, 'months').toDate();
                state.userSID = userSID;
                API.setUserSID(userSID);
                CookieJS.set(APP_CONFIG.USER_SID_COOKIE_NAME, userSID, {expires});
            } else {
                state.userSID = null;
                API.removeUserSID();
                CookieJS.remove(APP_CONFIG.USER_SID_COOKIE_NAME);
            }
        },
        savedBookingFormData(state, formData) {
            state.savedBookingFormData = Object.assign({}, formData);
        }
    },
     getters: {
        hasNavigated(state) {
            return (!_.isEmpty(state.travelQuery)) ? true : false;
        },
        currentNumberOfMissingOffers(state) {
            return state.totalOfferCount - state.offers.length;
        },
        totalOfferCount(state) {
            return state.totalOfferCount || state.offers.length;
        },
        travelQuery(state) {
            if(_.isEmpty(state.travelQuery)) {
                return { };
            }

            return Object.assign({}, state.travelQuery, {
                fromDate: (state.travelQuery.fromDate) ? Moment(state.travelQuery.fromDate, 'YYYY-MM-DD') : null,
                toDate: (state.travelQuery.toDate) ? Moment(state.travelQuery.toDate, 'YYYY-MM-DD') : null
            });
        },
        travelSearchQuery(state, getters) {
            return _.pick(getters.travelQuery, ['fromDate', 'toDate', 'departureAirport', 'destinationId', 'adults', 'childrenCount', 'children', 'flexibleStartDays', 'flexibleEndDays']);
        },
        travelFilterQuery(state, getters) {
            return _.pick(getters.travelQuery, [
                'minPrice',
                'maxPrice',
                'directFlight',
                'roomTypeCode',
                'boardTypeCode',
                'roomLocatedCode',
                'locationId',
                'keywords',
                'tourOperator'
            ]);
        },
        isLoggedIn(state) {
            return (state.authToken) ? true : false;
        },
        decodedToken(state) {
            if(!state.authToken) {
                return null;
            }

            return JwtDecode(state.authToken);
        }
    },
    actions: {
        addFavorite({dispatch, commit, getters}, hotelData) {
            return new Promise((resolve, reject) => {
                API.setFavorite(hotelData).then((res) => {
                    if(res.data.userSID && !getters.isLoggedIn) {
                        commit('userSID', res.data.userSID);
                    }

                    dispatch('getFavorites').then(() => {
                        resolve(res.data);
                    }).catch((error) => {
                        console.error(error);
                        reject();
                    });
                }).catch((error) => {
                    console.error(error);
                    reject();
                });
            });
        },
        removeFavorite({dispatch}, favoriteID) {
            return new Promise((resolve, reject) => {
                console.log('Dispatch remove favorite', favoriteID);
                API.deleteFavorite(favoriteID).then(() => {
                    console.log(favoriteID);
                    dispatch('getFavorites').then(() => {
                        resolve();
                    }).catch((error) => {
                        console.error(error);
                        reject();
                    });
                }).catch((error) => {
                    console.error(error);
                    reject();
                });
            });
        },
        getFavorites({commit, state}) {
            return new Promise((resolve, reject) => {
                if(state.userSID || state.authToken) {
                    API.getFavorites().then((res) => {
                        commit('favorites', res.data);
                        resolve();
                    }).catch((error) => {
                        console.error(error);
                        reject();
                    });
                } else {
                    reject();
                }
            });
        },
        isFavorite({state}, hotelId) {
            return new Promise((resolve, reject) => {
                if(!hotelId) {
                    reject();
                }

                let favorite = state.favorites.filter((favorite) => {
                    return _.propertyOf(favorite)(['hotel', 'id']) == hotelId;
                });

                if(favorite.length) {
                    resolve(favorite[0]);
                } else {
                    reject();
                }
            });
        },
        login({commit, dispatch}, {email, password, passwordResetToken}) {
            return new Promise((resolve, reject) => {
                API.getToken({email, password, passwordResetToken}).then((res) => {
                    commit('authToken', res.data.token);
                    commit('userSID', null);
                    dispatch('getUser');
                    dispatch('getFavorites');
                    resolve();
                }).catch((error) => {
                    console.error(error);
                    commit('authToken', null);
                    reject(error);
                });
            });
        },
        getUser({commit}) {
            return new Promise((resolve, reject) => {
                API.getUser().then((res) => {
                    commit('loggedInUser', res.data);
                    resolve(res.data);
                }).catch((error) => {
                    console.error(error);
                    reject();
                });
            });
        },
        logout({commit, dispatch}) {
            commit('authToken', null);
            commit('loggedInUser', { });
            commit('favorites', [ ]);
        },
        refreshLoginToken({commit, getters}) {
            return new Promise((resolve, reject) => {
                console.log('Refreshing token...');
                if(getters.isLoggedIn) {
                    API.refreshToken().then((res) => {
                        commit('authToken', res.data.token);
                        resolve();
                    }).catch((error) => {
                        console.error(error);
                        commit('authToken', null);
                        commit('loggedInUser', { });
                        reject();
                    });
                }
            });
        },
        getNextOffers({commit, state, dispatch}, query) {
            return new Promise((resolve, reject) => {
                commit('nextPage');
                dispatch('loadOffers', query).then(({data, headers}) => {
                    let offers = Object.assign([], state.offers);
                    offers = offers.concat(data);
                    commit('totalOfferCount', parseInt(headers['x-total-count']) || 0);
                    commit('offers', offers);
                    resolve(offers);
                }).catch((error) => {
                    console.error(error);
                    reject(error);
                });
            });
        },
        getOffers({commit, dispatch}, query) {
            return new Promise((resolve, reject) => {
                commit('offers', [ ]);
                commit('resetPage');
                dispatch('loadOffers', query).then(({data, headers}) => {
                    commit('totalOfferCount', parseInt(headers['x-total-count']) || 0);
                    commit('offers', data);
                    resolve(data);
                }).catch((error) => {
                    commit('totalOfferCount', 0);
                    console.error(error);
                    reject(error);
                });
            });
        },
        loadFilters({commit}, query) {
            return new Promise((resolve, reject) => {
                commit('filters', { });
                query = Object.assign({}, query);
                if(typeof query.children !== 'object') {
                    query.children = [query.children];
                }

                if(query.departureAirport) {
                    query.departureAirport = query.departureAirport.split(',');
                }

                API.getFilters(query).then((res) => {
                    commit('filters', res.data);
                    resolve();
                }).catch((error) => {
                    console.error(error);
                    reject(error);
                });
            });
        },
        loadBookingDraft({commit, state}, draftId) {
            return new Promise((resolve, reject) => {
                let mock = null;
                if(typeof window.MOCK_TYPE !== 'undefined' && !APP_CONFIG.IS_PRODUCTION && window.MOCK_TYPE !== 'ADMIN_REQUIRED') {
                    mock = window.MOCK_TYPE;
                }

                API.getBookingDraft(draftId, mock).then((res) => {
                    commit('selectedBookingOffer', Object.assign({}, res.data, {draftId}));
                    resolve();
                }).catch((error) => {
                    let extra = { };

                    if(error.response && error.response.status) {
                        extra.responsestatus = error.response.status;
                    }

                    if(error.response && error.response.data) {
                        extra.responsebody = error.response.data;
                    }

                    Sentry.captureException(error, {
                        extra,
                        tags: {
                            loadBookingDraft: true,
                            draftId
                        }
                    });
                    console.error(error);
                    reject(error);
                });
            });
        },
        updateBookingDraft({commit, state, dispatch}, {draftId, params}) {
            return new Promise((resolve, reject) => {
                API.updateBookingDraft(draftId, params).then((res) => {
                    dispatch('loadBookingDraft', draftId).then((res) => {
                        resolve();
                    }).catch((error) => {
                        reject(error);
                    });
                }).catch((error) => {
                    let extra = {
                        params: JSON.stringify(params)
                    }

                    if(error.response && error.response.status) {
                        extra.responsestatus = error.response.status;
                    }

                    if(error.response && error.response.data) {
                        extra.responsebody = error.response.data;
                    }

                    Sentry.captureException(error, {
                        extra,
                        tags: {
                            updateBookingDraft: true,
                            draftId
                        }
                    });
                    console.error(error);
                    reject(error);
                });
            });
        },
        makeBooking({commit}, draftId) {
            return new Promise((resolve, reject) => {
                let mock = null;
                if(typeof window.MOCK_TYPE !== 'undefined' && !APP_CONFIG.IS_PRODUCTION) {
                    mock = window.MOCK_TYPE;
                }

                API.makeBooking(draftId, mock).then((res) => {
                    if(_.contains(['OK', 'RQ', 'ADMIN_REQUIRED'], res.data.status)) {
                        commit('bookedOffer', res.data);
                        commit('selectedBookingOffer', { });
                        commit('selectedAgencyOffer', { });
                        resolve(res.data);
                    } else {
                        reject(res.data.status);
                    }
                }).catch((error) => {
                    let extra = {}

                    if(error.response && error.response.status) {
                        extra.responsestatus = error.response.status;
                    }

                    if(error.response && error.response.data) {
                        extra.responsebody = error.response.data;
                    }

                    Sentry.captureException(error, {
                        extra,
                        tags: {
                          makeBooking: true,
                          draftId
                        }
                    });

                    console.error(error);
                    reject(error);
                });
            });
        },
        loadOffers({commit, state}, query) { //Load offer data from query
            query = Object.assign({}, query);
            if(typeof query.children !== 'object') {
                query.children = [query.children];
            }

            if(query.departureAirport) {
                query.departureAirport = query.departureAirport.split(',');
            }

            query.perPage = query.perPage || APP_CONFIG.RESULT_PER_PAGE;
            query.page = state.currentPage || 1;

            return API.getOffers(query);
        },
        getDestination({commit}, params) {
            return new Promise((resolve, reject) => {
                API.getDestinationById(params.destinationId, params).then((res) => {
                    console.log('Data returned on destination');
                    commit('destination', res.data);
                    resolve();
                }).catch((error) => {
                    console.error(error);
                    reject(error);
                });
            });
        },
        getCountries({state, commit}) {
            return new Promise((resolve, reject) => {
                if(state.countries.length) {
                     resolve(state.countries);
                } else {
                    API.getCountries().then((res) => {
                        commit('countries', res.data);
                        resolve(res.data);
                    }).catch((error) => {
                        console.error(error);
                        reject();
                    });
                }
            });
        }
    }
});

const routes = [
    {path: '/', component: Frontpage, name: 'Frontpage'},
    {path: '/hotels', component: HotelPage, name: 'Hotels'},
    {path: '/offers/:hotelid', component: AgencyPage, name: 'Offers'},
    {path: '/booking/:draftid', component: BookingPage, name: 'Booking'},
    {path: '/booking/:draftid/order', component: OrderPage, name: 'Order'},
    {path: '/myskov/:page?', component: MySkov, name: 'Myskov'},
];

const router = new VueRouter({
    // mode: 'history', this will remove the /#/ from URL.
    routes, 
    scrollBehavior (to, from, savedPosition) {
        if(from.path !== to.path) {
            if (savedPosition) {
                return savedPosition;
            } else {
                return { x: 0, y: 0 };
            }
        }
    }
});

// GOOGLE Analytics
Vue.use(VueAnalytics, {
    id: APP_CONFIG.GOOGLE_ANALYTICS_KEY,
    router,
    ecommerce: {
        enabled: true
    },
    debug: {
        enabled: !APP_CONFIG.IS_PRODUCTION,
        sendHitTask: APP_CONFIG.IS_PRODUCTION
    }
});

// GOOGLE Tag manager
if(APP_CONFIG.GOOGLE_TAG_MANAGER_KEY) {
    Vue.use(VueGtm, {
        id: APP_CONFIG.GOOGLE_TAG_MANAGER_KEY, // Your GTM single container ID, array of container ids ['GTM-xxxxxx', 'GTM-yyyyyy'] or array of objects [{id: 'GTM-xxxxxx', queryParams: { gtm_auth: 'abc123', gtm_preview: 'env-4', gtm_cookies_win: 'x'}}, {id: 'GTM-yyyyyy', queryParams: {gtm_auth: 'abc234', gtm_preview: 'env-5', gtm_cookies_win: 'x'}}], // Your GTM single container ID or array of container ids ['GTM-xxxxxx', 'GTM-yyyyyy']
        queryParams: { },
        enabled: false, // defaults to true. Plugin can be disabled by setting this to false for Ex: enabled: !!GDPR_Cookie (optional)
        debug: !APP_CONFIG.IS_PRODUCTION, // Whether or not display console logs debugs (optional)
        loadScript: true, // Whether or not to load the GTM Script (Helpful if you are including GTM manually, but need the dataLayer functionality in your components) (optional)
        vueRouter: router, // Pass the router instance to automatically sync with router (optional)
        ignoredViews: [ ], // Don't trigger events for specified router names (optional)
        trackOnNextTick: false, // Whether or not call trackView in Vue.nextTick
    });   
}

if(APP_CONFIG.ENABLE_SENTRY) {
    console.debug('Enabling Sentry for', APP_CONFIG.SENTRY_ENVIRONMENT);
    Sentry.init({
        Vue,
        dsn: APP_CONFIG.SENTRYKEY,
        environment: APP_CONFIG.SENTRY_ENVIRONMENT,
        integrations: [
          new BrowserTracing({
            routingInstrumentation: Sentry.vueRouterInstrumentation(router)
          }),
          new CaptureConsole({
            levels: ['error']
          })
        ],
        // Set tracesSampleRate to 1.0 to capture 100%
        // of transactions for performance monitoring.
        // We recommend adjusting this value in production
        tracesSampleRate: 1.0,
      });
}

window.MAIN_APP = new Vue({
    el: '#app',
    router,
    store,
    created() {
        let token = window.APP_CONFIG.USER_TOKEN || CookieJS.get(APP_CONFIG.AUTH_COOKIE_NAME);
        if(token) {
            this.$store.commit('authToken', token);
            this.$store.dispatch('refreshLoginToken').then(() => {
                this.$store.dispatch('getUser');
                this.$store.dispatch('getFavorites');
                this.runLoginTimer();
            }).catch(() => {
                this.checkUserSID();
            });
        } else {
            this.runLoginTimer();
            this.checkUserSID();
        }
        
        API.setLogOutHandler((response) => {
            this.$store.commit('authToken', null);
            this.$store.commit('loggedInUser', null);
        });

        window.addEventListener('CookiebotOnAccept', this.checkCookieConsent, false);
        window.addEventListener('CookiebotOnDecline', this.checkCookieConsent, false);
        this.checkCookieConsent();
    },
    mounted() {
        this.checkIntercom();
    },
    beforeDestroy() {
        window.removeEventListener('CookiebotOnAccept', this.checkCookieConsent);
        window.removeEventListener('CookiebotOnDecline', this.checkCookieConsent);
    },
    data() {
        return {
            jwtTimeout: null,
            isReady: true,
            showUspModalIdx: -1
        };
    },
    methods: {
        checkUserSID() {
            if(!this.isLoggedIn) {
                let userSID = CookieJS.get(APP_CONFIG.USER_SID_COOKIE_NAME);
                if(userSID) {
                    this.$store.commit('userSID', userSID);
                    this.$store.dispatch('getFavorites');
                }
            }
        },
        checkCookieConsent() {
            if(
                    typeof Cookiebot !== 'undefined' && 
                    Cookiebot && 
                    typeof Cookiebot.consent !== 'undefined' && 
                    Cookiebot.consent && 
                    Cookiebot.consent.statistics
            ) {
                console.log('Enable GA & GTM');
                this.$ga.enable();
                if(this.$gtm) {
                    this.$gtm.enable(true);
                }
            } else {
                console.log('Disable GA & GTM');
                this.$ga.disable();
                if(this.$gtm) {
                    this.$gtm.enable(false);
                }
            }
        },
        runLoginTimer() {
            if(this.isLoggedIn) {
                console.log('Starting timer...');
                this.jwtTimeout = setInterval(() => {
                    this.$store.dispatch('refreshLoginToken');
                }, APP_CONFIG.JWT_TIMEOUT * 1000);
            } else if(this.jwtTimeout) {
                console.log('Ending timer');
                clearInterval(this.jwtTimeout);
            }
        },
        showIntercom() {
            if(window.Intercom) {
                window.Intercom('update', {
                    hide_default_launcher: false
                });
            }
        },
        hideIntercom() {
            if(window.Intercom) {
                window.Intercom('update', {
                    hide_default_launcher: true
                });
            }
        },
        checkIntercom() {
            if(this.isFrontpage) {
                this.showIntercom();
            } else {
                this.hideIntercom();
            }
        }
    },
    watch: {
        isLoggedIn() {
            this.runLoginTimer();
        },
        isFrontpage() {
            this.checkIntercom();
        }
    },
    computed: {
        isFrontpage() {
            return this.$route.name === 'Frontpage';
        },
        isLoggedIn() {
            return this.$store.getters.isLoggedIn;
        }
    },
    components: {
        search,
        NewsletterSignup,
        Carousel,
        Slide,
        MsgBar,
        PopularDestinations,
        PopularFilters,
        TrustpilotBlock,
        PageSearchWidgetModal,
        Modal
    }
});
